import { CarbonUnit, UnitNumberType } from "./units/units";
import {
  CertifiedMetricBooleanId,
  CertifiedMetricNumberId,
  CycleSlot,
  Metric,
  MetricBoolean,
  MetricEnum,
  MetricList,
  MetricNumber,
  MetricString,
  MetricValidation,
} from "./metrics";
import { clamp } from "./math";
import { DailyMetricResponse, WeedMetrics } from "protos/portal/metrics";
import { entryTimingGradeLabel } from "./entryTimingGrade";
import { isUndefined } from "./identity";
import { ReportMode } from "../../../../protos/golang/generated/ts2/portal/reports";
import { TFunction } from "i18next";
import { translateIfKeyExists } from "common/utils/i18n";
import { WEED_FRACTION_OTHER_KEY } from "./hooks/usePlantCategoryNames";

export const getMetricName = (t: TFunction, metric: Metric): string => {
  // carbon.actions.compareKeys.ignoreDynamic
  return t(`utils.metrics.certified.metrics.${metric.i18nId ?? metric.id}`);
};

export const getMetricHelp = (
  t: TFunction,
  metric: Metric
): string | undefined => {
  return translateIfKeyExists(t, {
    // carbon.actions.compareKeys.ignoreDynamic
    i18nKey: `utils.metrics.certified.metricsHelp.${
      metric.i18nId ?? metric.id
    }`,
  });
};

const DEFAULT_METRIC: Pick<Metric, "canAverage" | "canTotal" | "validation"> = {
  canAverage: true,
  canTotal: true,
  validation: MetricValidation.PENDING,
};

export const metricBoolean = (
  id: CertifiedMetricBooleanId,
  i18nKey: string,
  overrides: Partial<MetricBoolean> = {}
): MetricBoolean => ({
  ...DEFAULT_METRIC,
  id,
  type: "boolean",
  i18nKey,
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
  getValue: (metrics) => metrics[id],
  ...overrides,
});

const metricNumber = (
  id: CertifiedMetricNumberId,
  cycleSlot: CycleSlot | undefined,
  units: CarbonUnit = UnitNumberType.COUNT,
  overrides: Partial<MetricNumber> = {}
): MetricNumber => ({
  ...DEFAULT_METRIC,
  id,
  type: "number",
  units,
  validation: MetricValidation.VALIDATED,
  cycleSlot,
  getValue: (metrics) => metrics[id],
  ...overrides,
});

export const METRIC_ACRES_WEEDED = metricNumber(
  "acresWeeded",
  CycleSlot.AREA,
  "ac"
);

export const METRIC_AVERAGE_SPEED = metricNumber(
  "avgSpeedMph",
  CycleSlot.SPEED,
  "mph",
  {
    canTotal: false,
  }
);

export const METRIC_AVERAGE_WEED_SIZE = metricNumber(
  "avgWeedSizeMm",
  CycleSlot.SIZE,
  "mm",
  {
    canTotal: false,
  }
);

// VIRTUAL METRIC: avgWeedSizeM is not a real metric,
// but is for use only with the PlantProfileMetrics list.
// Consider re-architecting later.
export const METRIC_AVERAGE_WEED_SIZE_METER: MetricNumber = {
  ...METRIC_AVERAGE_WEED_SIZE,
  units: "m",
};

export const METRIC_AVERAGE_CROP_SIZE = metricNumber(
  "avgCropSizeMm",
  CycleSlot.SIZE,
  "mm",
  {
    canTotal: false,
  }
);

// VIRTUAL METRIC: avgCropSizeM is not a real metric,
// but is for use only with the CropMetrics map.
// Our metric display components tend to accept whole metric objects, despite
// only using id and i18nId. We probably wouldn't need this if that were fixed.
// Consider re-architecting later.
export const METRIC_AVERAGE_CROP_SIZE_METER: MetricNumber = {
  ...METRIC_AVERAGE_CROP_SIZE,
  units: "m",
};

export const METRIC_AVERAGE_TARGETABLE_REQ_LASER_TIME = metricNumber(
  "avgTargetableReqLaserTime",
  CycleSlot.SHOOT,
  "ms",
  {
    canTotal: false,
  }
);

export const METRIC_BANDING_CONFIG: MetricString = {
  ...DEFAULT_METRIC,
  id: "bandingConfigName",
  type: "string",
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
  getValue: (metrics) => metrics.bandingConfigName,
};

export const METRIC_BANDING_ENABLED = metricBoolean(
  "bandingEnabled",
  "enabled"
);

export const METRIC_BANDING_PROFILES: MetricList = {
  ...DEFAULT_METRIC,
  id: "bandingProfiles",
  type: "list",
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
  getValue: (metrics) => metrics.bandingProfiles,
};

export const METRIC_COVERAGE_SPEED = metricNumber(
  "coverageSpeedAcresHr",
  CycleSlot.COVERAGE,
  "ac/h",
  {
    canTotal: false,
  }
);

export const METRIC_DISTANCE_WEEDED = metricNumber(
  "distanceWeededMeters",
  CycleSlot.DISTANCE,
  "m"
);

export const METRIC_KILLED_WEEDS = metricNumber("killedWeeds", undefined);

export const METRIC_MISSED_WEEDS = metricNumber("missedWeeds", undefined);

export const METRIC_SKIPPED_WEEDS = metricNumber("skippedWeeds", undefined);

export const METRIC_TIME_EFFICIENCY = metricNumber(
  "timeEfficiency",
  undefined,
  UnitNumberType.PERCENT,
  {
    getValue: (metrics) => metrics.timeEfficiency * 100,
    canTotal: false,
  }
);

export const METRIC_TOTAL_WEEDS = metricNumber(
  "totalWeeds",
  undefined,
  UnitNumberType.COUNT,
  {
    validation: MetricValidation.INTERNAL,
  }
);

export const METRIC_TOTAL_WEEDS_IN_BAND = metricNumber(
  "totalWeedsInBand",
  undefined
);

export const METRIC_UPTIME = metricNumber(
  "uptimeSeconds",
  CycleSlot.TIME_LIMITED,
  "s"
);

export const METRIC_WEED_DENSITY = metricNumber(
  "weedDensitySqFt",
  CycleSlot.DENSITY,
  "/ft2",
  {
    canTotal: false,
  }
);

// VIRTUAL METRIC: weedDensitySqM is not a real metric,
// but is for use only with the PlantProfileMetrics list.
// Consider re-architecting later.
export const METRIC_WEED_DENSITY_SQ_METER: MetricNumber = {
  ...METRIC_WEED_DENSITY,
  units: "/m2",
};

export const METRIC_CROP_DENSITY = metricNumber(
  "cropDensitySqFt",
  CycleSlot.DENSITY,
  "/ft2",
  {
    canTotal: false,
  }
);

// VIRTUAL METRIC: cropDensitySqM is not a real metric,
// but is for use only with the CropMetrics map.
// Our metric display components tend to accept whole metric objects, despite
// only using id and i18nId. We probably wouldn't need this if that were fixed.
// Consider re-architecting later.
export const METRIC_CROP_DENSITY_SQ_METER: MetricNumber = {
  ...METRIC_CROP_DENSITY,
  units: "/m2",
};

export const METRIC_WEEDING_EFFICIENCY = metricNumber(
  "weedingEfficiency",
  undefined,
  UnitNumberType.PERCENT,
  {
    getValue: (metrics) => metrics.weedingEfficiency * 100,
    canTotal: false,
  }
);

export const METRIC_WEEDING_TIME = metricNumber(
  "weedingUptimeSeconds",
  CycleSlot.TIME_LIMITED,
  "s"
);

export const METRIC_TARGET_WEEDING_TIME = metricNumber(
  "targetWeedingTimeSeconds",
  CycleSlot.TIME_LIMITED,
  "s",
  {
    validation: MetricValidation.PENDING,
  }
);

// The backend sends weed type breakdowns as fractions (0-1).
// It returns 0 for all four when no weeds were
// categorized; show those as "no data" rather than 0%.
const getWeedsTypeFraction = (
  key:
    | "weedsTypeFractionBroadleaf"
    | "weedsTypeFractionGrass"
    | "weedsTypeFractionOffshoot"
    | "weedsTypeFractionPurslane"
): MetricNumber =>
  metricNumber(key, undefined, UnitNumberType.PERCENT, {
    canTotal: false,
    getValue: (metrics: DailyMetricResponse): number | undefined => {
      const totalCategorized =
        metrics.weedsTypeFractionBroadleaf +
        metrics.weedsTypeFractionGrass +
        metrics.weedsTypeFractionOffshoot +
        metrics.weedsTypeFractionPurslane;
      if (isUndefined(metrics[key]) || !totalCategorized) {
        return;
      }
      return metrics[key] * 100;
    },
    i18nId: key.replace("Fraction", "Count"),
  });

export const METRIC_FRACTION_BROADLEAF = getWeedsTypeFraction(
  "weedsTypeFractionBroadleaf"
);

export const METRIC_FRACTION_GRASS = getWeedsTypeFraction(
  "weedsTypeFractionGrass"
);

export const METRIC_FRACTION_PURSLANE = getWeedsTypeFraction(
  "weedsTypeFractionPurslane"
);

export const METRIC_FRACTION_OFFSHOOT = getWeedsTypeFraction(
  "weedsTypeFractionOffshoot"
);

// Report column combining all per-category weed fractions
// (weedMetricsByCategory) into one list; replaces the four fixed fraction
// columns when jigsaw metrics are on (see getReportMetrics). Not in
// CERTIFIED_METRICS: the report table renders it from the raw map via
// sortedWeedTypeEntries rather than through getValue.
export const METRIC_WEED_TYPES: MetricList = {
  ...DEFAULT_METRIC,
  id: "weedMetricsByCategory",
  type: "list",
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
  getValue: (metrics) =>
    sortedWeedTypeEntries(metrics.weedMetricsByCategory).map(
      ({ id, weed }) => ({
        id,
        name: weed.localizedName,
        percent: weed.fractionOfTotal * 100,
      })
    ),
};

// Report column for the entry timing grade. The value stays the protobuf enum
// so aggregates compare grades; formatValue turns it into a label.
export const METRIC_ENTRY_TIMING: MetricEnum = {
  ...DEFAULT_METRIC,
  id: "entryTimingGrade",
  i18nId: "entryTiming",
  type: "enum",
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
  getValue: (metrics) => metrics.entryTimingGrade,
  formatValue: (value, t) => entryTimingGradeLabel(t, value),
};

export interface WeedTypeEntry {
  id: string;
  weed: WeedMetrics;
}

// Entries of a weedMetricsByCategory map ordered for display: largest
// fraction first with the combined "other" bucket pinned last, zero
// fractions dropped (the legacy back-fill emits all four veselka categories
// even when nothing was categorized).
export const sortedWeedTypeEntries = (
  weedMetricsByCategory: Record<string, WeedMetrics> | undefined
): WeedTypeEntry[] =>
  Object.entries(weedMetricsByCategory ?? {})
    .filter(([, weed]) => weed.fractionOfTotal > 0)
    .sort(([aId, a], [bId, b]) => {
      if (aId === WEED_FRACTION_OTHER_KEY) {
        return 1;
      }
      if (bId === WEED_FRACTION_OTHER_KEY) {
        return -1;
      }
      return b.fractionOfTotal - a.fractionOfTotal;
    })
    .map(([id, weed]) => ({ id, weed }));

export const METRIC_THINNED_CROPS = metricNumber("thinnedCrops", undefined);
export const METRIC_SKIPPED_CROPS = metricNumber("skippedCrops", undefined);
export const METRIC_MISSED_CROPS = metricNumber("missedCrops", undefined);
export const METRIC_TOTAL_CROPS = metricNumber(
  "totalCrops",
  undefined,
  UnitNumberType.COUNT,
  {
    validation: MetricValidation.INTERNAL,
  }
);
export const METRIC_VALID_CROPS = metricNumber(
  "validCrops",
  undefined,
  UnitNumberType.COUNT,
  {
    getValue: (metrics) => metrics.validCrops || metrics.totalCrops,
  }
);
export const METRIC_KEPT_CROPS = metricNumber("keptCrops", undefined);

export const METRIC_BANDING_PERCENTAGE = metricNumber(
  "bandingPercentage",
  undefined,
  UnitNumberType.PERCENT,
  {
    canTotal: false,
    getValue: (metrics) => clamp(0, 100, metrics.bandingPercentage),
  }
);

export const METRIC_THINNING_EFFICIENCY = metricNumber(
  "thinningEfficiency",
  undefined,
  UnitNumberType.PERCENT,
  {
    getValue: (metrics) => metrics.thinningEfficiency * 100,
    canTotal: false,
    i18nId: "thinningPerformance",
  }
);

export const METRIC_OPERATOR_EFFECTIVENESS = metricNumber(
  "operatorEffectiveness",
  undefined,
  UnitNumberType.PERCENT,
  {
    getValue: (metrics) => metrics.operatorEffectiveness * 100,
    canTotal: false,
    validation: MetricValidation.PENDING,
  }
);

export const METRIC_OVERALL_EFFICIENCY = metricNumber(
  "overallEfficiency",
  undefined,
  UnitNumberType.PERCENT,
  {
    getValue: (metrics) => metrics.overallEfficiency * 100,
    canTotal: false,
    i18nId: "overallPerformance",
  }
);

export const METRIC_CROP: MetricString = {
  ...DEFAULT_METRIC,
  id: "crop",
  type: "string",
  getValue: ({ latestCrop }) => {
    return latestCrop?.localizedName;
  },
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
};

export const METRIC_CROPS: MetricList = {
  ...DEFAULT_METRIC,
  id: "crops",
  type: "list",
  getValue: (metrics) =>
    metrics.crops.map((v) => ({
      id: v.id,
      name: v.localizedName,
    })),
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
};

export const METRIC_JOB_NAME: MetricString = {
  ...DEFAULT_METRIC,
  id: "jobName",
  type: "string",
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
  getValue: (metrics) => metrics.jobName,
};

export const METRIC_JOBS: MetricList = {
  ...DEFAULT_METRIC,
  id: "jobs",
  type: "list",
  canTotal: false,
  canAverage: false,
  validation: MetricValidation.VALIDATED,
  getValue: (metrics) =>
    "jobs" in metrics
      ? metrics.jobs
      : metrics.jobId
      ? [{ id: metrics.jobId, name: metrics.jobName }]
      : [],
};

export const CERTIFIED_METRICS = [
  METRIC_CROP,
  METRIC_JOB_NAME,
  METRIC_UPTIME,
  METRIC_WEEDING_TIME,
  METRIC_TARGET_WEEDING_TIME,
  METRIC_TIME_EFFICIENCY,
  METRIC_ACRES_WEEDED,
  METRIC_COVERAGE_SPEED,
  METRIC_AVERAGE_SPEED,
  METRIC_OPERATOR_EFFECTIVENESS,
  METRIC_DISTANCE_WEEDED,
  METRIC_BANDING_CONFIG,
  METRIC_BANDING_ENABLED,
  METRIC_AVERAGE_WEED_SIZE,
  METRIC_ENTRY_TIMING,
  METRIC_AVERAGE_CROP_SIZE,
  METRIC_AVERAGE_TARGETABLE_REQ_LASER_TIME,
  METRIC_WEED_DENSITY,
  METRIC_FRACTION_BROADLEAF,
  METRIC_FRACTION_GRASS,
  METRIC_FRACTION_PURSLANE,
  METRIC_FRACTION_OFFSHOOT,
  METRIC_TOTAL_WEEDS,
  METRIC_TOTAL_WEEDS_IN_BAND,
  METRIC_KILLED_WEEDS,
  METRIC_WEEDING_EFFICIENCY,
  METRIC_SKIPPED_WEEDS,
  METRIC_MISSED_WEEDS,
  METRIC_CROP_DENSITY,
  METRIC_THINNED_CROPS,
  METRIC_SKIPPED_CROPS,
  METRIC_MISSED_CROPS,
  METRIC_TOTAL_CROPS,
  METRIC_VALID_CROPS,
  METRIC_KEPT_CROPS,
  METRIC_BANDING_PERCENTAGE,
  METRIC_THINNING_EFFICIENCY,
  METRIC_OVERALL_EFFICIENCY,
];

const metricsById: Map<string, Metric> = new Map();
for (const metric of CERTIFIED_METRICS) {
  metricsById.set(metric.id, metric);
}
// Resolvable by id for report columns, but kept out of CERTIFIED_METRICS so
// it is never offered outside jigsaw reports (see getReportMetrics).
metricsById.set(METRIC_WEED_TYPES.id, METRIC_WEED_TYPES);

export const getMetricById = (
  id: string,
  mode: ReportMode
): Metric | undefined =>
  mode === ReportMode.REPORT_MODE_JOBS && id === METRIC_JOB_NAME.id
    ? undefined
    : metricsById.get(id);

const WEED_TYPE_FRACTION_METRICS: Set<Metric> = new Set([
  METRIC_FRACTION_BROADLEAF,
  METRIC_FRACTION_GRASS,
  METRIC_FRACTION_PURSLANE,
  METRIC_FRACTION_OFFSHOOT,
]);
const JOB_METRICS = new Set<Metric>([METRIC_JOB_NAME, METRIC_JOBS]);

// The fleet view grid renders boolean, number, and string columns only, so
// enum and list metrics are offered on reports only.
export type FleetViewMetric = MetricBoolean | MetricNumber | MetricString;

export const isFleetViewMetric = (metric: Metric): metric is FleetViewMetric =>
  metric.type !== "enum" && metric.type !== "list";

/**
 * @returns the metrics that can be viewed on the fleet view.
 * Job is hidden because there's a built-in column.
 * Weed Type Fraction columns are hidden if Jigsaw is on, (fleet view doesn't
 * support list columns which would be needed to display Custom Category data)
 **/
export const getFleetViewMetrics = (useJigsaw: boolean): FleetViewMetric[] =>
  CERTIFIED_METRICS.filter((metric): metric is FleetViewMetric =>
    isFleetViewMetric(metric)
  ).filter(
    (metric) =>
      !JOB_METRICS.has(metric) &&
      (!useJigsaw || !WEED_TYPE_FRACTION_METRICS.has(metric))
  );

// The customer feature flags that gate individual report columns. Undefined
// means the flag is still loading, which counts as off.
export interface MetricFlags {
  entryTimingEnabled: boolean | undefined;
}

const FLAGGED_METRIC_IDS: Map<string, keyof MetricFlags> = new Map([
  [METRIC_ENTRY_TIMING.id, "entryTimingEnabled"],
]);

/**
 * @returns whether a report column is available to the customer. Columns
 * without a feature flag are always available.
 **/
export const isMetricEnabled = (id: string, flags: MetricFlags): boolean => {
  const flag = FLAGGED_METRIC_IDS.get(id);
  return flag === undefined || flags[flag] === true;
};

// Metrics offerable as report columns. With jigsaw metrics on, the four
// fixed weed-type fraction columns are replaced in place by the combined
// per-category weed types column. Fleet view filters by metric type instead
// and does not use this replacement.
export const getReportMetrics = (
  mode: ReportMode,
  options: MetricFlags & { useJigsaw: boolean }
): Metric[] => {
  const { useJigsaw } = options;
  const byMode =
    mode === ReportMode.REPORT_MODE_DAYS || !useJigsaw
      ? CERTIFIED_METRICS
      : CERTIFIED_METRICS.filter((metric) => metric !== METRIC_JOB_NAME);
  const metrics = useJigsaw
    ? byMode.flatMap((metric): Metric[] => {
        if (metric === METRIC_FRACTION_BROADLEAF) {
          return [METRIC_WEED_TYPES];
        }
        return WEED_TYPE_FRACTION_METRICS.has(metric) ? [] : [metric];
      })
    : byMode;
  return metrics.filter((metric) => isMetricEnabled(metric.id, options));
};
